<template>
  <div
    class="hall"
    :class="`hall_zoom_${zoom}`"
  >
    <div ref="container" class="hall__content">
      <div class="hall__controls">
        <BaseIconButton
          icon="plus"
          class="hall__control"
          :disabled="!isZoomInEnable"
          @click="zoomIn"
        />
        <BaseIconButton
          icon="minus"
          class="hall__control"
          :disabled="!isZoomOutEnable"
          @click="zoomOut"
        />
      </div>
      <div ref="scheme" class="hall__scheme pretty-scrollbar">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          :viewBox="`0 0 ${canvas.width} ${canvas.height}`"
          :width="currentSize.width"
          :height="currentSize.height"
          class="hall__canvas"
          @click="onSchemeClick"
        >

          <!-- Рисуем доп. элементы -->
          <template v-for="(item, index) in extraElements">
            <text
              v-if="checkIsText(item)"
              v-bind="getSchemeText(item)"
              :key="`extra_item_${index}`"
            >
              {{ item.text }}
            </text>
            <template v-else>
              <rect
                v-bind="getSchemeRect(item)"
                :key="`extra_item_rect_${index}`"
              />
              <text
                v-if="item.text"
                v-bind="getSchemeText(item)"
                :key="`extra_item_text_${index}`"
              >
                {{ item.text }}
              </text>
            </template>
          </template>

          <!-- Рисуем зоны и места -->
          <g v-for="area in scheme.areas" :key="`area_${area.id}`">
            <g
              v-for="seat in area.seats"
              :key="`area_${area.id}_${seat.id}`"
              :transform="getSeatPosition(seat)"
              :class="getSeatModifiers(seat)"
            >
              <rect v-bind="getSeatRect(seat, area)" />
              <text v-bind="getSeatText(seat, area)" >
                {{ seat.seat }}
              </text>
            </g>
          </g>

        </svg>
      </div>
    </div>
    <div class="hall__footer">
      <div class="hall__prices" :class="{ hall__prices_many: prices.length > 5 }">
        <div
          v-for="item in prices"
          :key="`price_${item.price}`"
          class="hall__price"
          :style="{ background: item.color }"
        >
          {{ item.price | sum }}
        </div>
      </div>
      <BaseTitle
        v-if="totalText"
        :text="totalText"
        :is-fat="false"
        is-normal
        is-up
        class="hall__total"
      />
      <div v-if="$slots.actions" class="hall__actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<script>
import BaseTitle from '@/components/BaseTitle.vue';
import BaseIconButton from '@/components/BaseIconButton.vue';

export default {
  name: 'Hall',
  components: {
    BaseTitle,
    BaseIconButton,
  },
  props: {
    grid: {
      type: Number,
      default: 60,
    },
    eventId: {
      type: String,
      required: true,
    },
    scheme: {
      type: Object,
      required: true,
    },
    tickets: {
      type: Array,
      default: () => [],
    },
    availableSeats: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      zoom: 0,
      containerWidth: 0,
      containerHeight: 0,
    };
  },
  computed: {
    extraElements() {
      return [...this.scheme.extraElements]
        .sort((one, two) => (Number(one.zIndex) - Number(two.zIndex)));
    },
    isZoomInEnable() {
      const nextSize = this.sizes[this.zoom + 1];
      return Boolean(nextSize);
    },
    isZoomOutEnable() {
      const prevSize = this.sizes[this.zoom - 1];
      return Boolean(prevSize);
    },
    currentSize() {
      return this.sizes[this.zoom];
    },
    sizes() {
      const sizes = [];
      const step = 10;
      const widthStep = this.canvas.width / step;
      const heightStep = this.canvas.height / step;
      const { containerWidth, containerHeight } = this;

      for (let i = 0; i < 100; i += 1) {
        const width = this.canvas.width - (i * widthStep);
        const height = this.canvas.height - (i * heightStep);
        if (width > 0 && height > 0) {
          sizes.unshift({ width, height });
        }
        if (width <= containerWidth && height <= containerHeight) {
          break;
        }
      }
      return sizes;
    },
    prices() {
      let colors = [
        '#95fec3',
        '#fcc3bb',
        '#d4e8a5',
        '#efd7ef',
        '#aaeda0',
        '#f2f394',
        '#ead8a5',
        '#9bbd9b',
      ];
      const fallbackColor = '#e8b8e4';
      const items = this.availableSeats
        .reduce((acc, { price }) => {
          if (acc[price]) return acc;

          // Берем первый цвет из массива и затем удаляем его
          const [firstColor] = colors;
          colors = colors.slice(1);

          return { ...acc, [price]: { price, color: firstColor || fallbackColor } };
        }, {});
      return Object.values(items);
    },
    canvas() {
      const { extraElements = [], areas = [] } = this.scheme;
      const seats = areas.map((el) => el.seats).flat();
      const items = [...extraElements, ...seats];
      const offsets = items
        .map((el) => Number(el.pos_x || 0));
      const minOffset = Math.min(...offsets);

      return items.reduce((acc, el) => {
        const fullWidth = Number(el.pos_x || 0) + Number(el.width || 1) + minOffset;
        const fullHeight = Number(el.pos_y || 0) + Number(el.height || 1);
        const elWidth = fullWidth * this.grid;
        const elHeight = fullHeight * this.grid;
        return {
          width: elWidth > acc.width ? elWidth : acc.width,
          height: elHeight > acc.height ? elHeight : acc.height,
        };
      }, { width: 0, height: 0 });
    },
    availableSeatsIds() {
      return this.availableSeats.map((el) => el.id);
    },
    ticketsIds() {
      return this.tickets.map((el) => el.seatId);
    },
    ticketsCountText() {
      return this.$tc('pluralization.ticket', this.tickets.length);
    },
    sum() {
      return this.tickets.reduce((acc, el) => acc + el.price, 0);
    },
    totalText() {
      if (this.tickets.length === 0) {
        return '';
      }
      return `${this.ticketsCountText},${this.$options.filters.sum(this.sum, true)}`;
    },
  },
  mounted() {
    this.setContainerSize();
  },
  methods: {
    setContainerSize() {
      this.containerWidth = this.$refs.container?.clientWidth || 0;
      this.containerHeight = this.$refs.container?.clientHeight || 0;
    },
    zoomIn() {
      this.zoom += 1;
      this.updateSchemeScroll();
    },
    zoomOut() {
      this.zoom -= 1;
      this.updateSchemeScroll();
    },
    updateSchemeScroll() {
      const { scheme } = this.$refs;
      if (!scheme) return;
      const {
        scrollWidth,
        scrollLeft,
        clientWidth,
        scrollHeight,
        scrollTop,
        clientHeight,
      } = scheme;
      const leftScrollPercent = Math.round(scrollLeft / ((scrollWidth - clientWidth) / 100));
      const topScrollPercent = Math.round(scrollTop / ((scrollHeight - clientHeight) / 100));
      this.$nextTick(() => {
        if (!scheme) return;
        const [width, height] = [
          scheme.scrollWidth - scheme.clientWidth,
          scheme.scrollHeight - scheme.clientHeight,
        ];
        scheme.scrollTop = Math.round(topScrollPercent * (height / 100));
        scheme.scrollLeft = Math.round(leftScrollPercent * (width / 100));
      });
    },
    checkIsText(item) {
      return item.type === 'text';
    },
    getSize(value, offset) {
      return (Number(value || 1) + (Number(offset || 0) / 2)) * this.grid;
    },
    getSchemeRect(item) {
      const [x, y, width, height, rx] = [
        this.getSize(item.pos_x),
        this.getSize(item.pos_y),
        this.getSize(item.width),
        this.getSize(item.height),
        item.type === 'circle' ? Math.max(item.width, item.height) : 10,
      ];
      return {
        x,
        y,
        rx,
        width,
        height,
        fill: item.background || false,
        opacity: item.opacity && String(item.opacity) !== '1' ? item.opacity : false,
        class: item.class,
      };
    },
    getSchemeText(item) {
      const [x, y] = [
        this.getSize(item.pos_x, item.width || 1),
        this.getSize(item.pos_y, item.height || 1),
      ];
      return {
        x,
        y,
        opacity: item.opacity && String(item.opacity) !== '1' ? item.opacity : false,
        'alignment-baseline': 'middle',
        'text-anchor': 'middle',
        class: item.class === 'row-label' ? 'hall__label' : 'hall__text',
        'font-size': item.fontSize && String(item.fontSize) !== '1' ? `${item.fontSize}em` : false,
      };
    },
    getSeatPosition(seat) {
      const [x, y] = [this.getSize(seat.pos_x), this.getSize(seat.pos_y)];
      const rotation = seat.rotation ? ` rotate(${seat.rotation})` : '';
      return `translate(${x}, ${y})${rotation}`;
    },
    getSeatModifiers(seat) {
      const id = String(seat.id);
      return {
        hall__seat: true,
        hall__seat_disabled: !this.availableSeatsIds.includes(id),
        hall__seat_selected: this.ticketsIds.includes(id),
      };
    },
    getSeatRect(seat) {
      const margin = 4;
      const size = this.grid - (margin * 2);
      const id = String(seat.id);
      const isAvailable = this.availableSeatsIds.includes(id);
      const fill = isAvailable ? this.findColorBySeatId(id) : false;
      return {
        x: margin,
        y: margin,
        rx: size / 5,
        width: size,
        height: size,
        fill,
        'data-seat-id': seat.id,
        'data-row': seat.row,
        'data-seat': seat.seat,
      };
    },
    getSeatText() {
      const margin = this.grid / 2;
      return {
        x: margin,
        y: margin,
        'alignment-baseline': 'middle',
        'text-anchor': 'middle',
      };
    },
    onSchemeClick(e) {
      const { row = '', seat = '', seatId } = e.target.dataset;
      if (!seatId) return;
      const index = this.tickets.findIndex((el) => el.seatId === seatId);
      if (index >= 0) {
        this.$delete(this.tickets, index);
      } else {
        const { price } = this.availableSeats.find(({ id }) => id === seatId) || {};
        this.tickets.push({
          id: seatId,
          row,
          seat,
          price,
          seatId,
          eventId: this.eventId,
        });
      }
    },
    findColorBySeatId(id) {
      const seat = this.availableSeats.find((el) => el.id === id);
      const price = seat ? this.prices.find((el) => el.price === seat.price) : null;
      return price?.color;
    },
  },
};
</script>

<style lang="stylus">
.hall
  display block
  width 100%
  height 100%

  &__content
    position relative
    height calc(100% - 5em)
    background-color #f1f1f1
    overflow hidden

  &__footer
    display flex
    align-items center
    justify-content flex-end
    height 5em
    padding 1em 1.5em

  &__controls
    abs top right
    margin 2em
    z-index 10

  &__control
    position relative
    margin-top 1.8em
    color #fff
    z-index 1
    &:first-child
      margin-top 0
    &:after
      background-color $c_bright
      border-radius 10em
      margin -0.7em
      z-index -1
    &:hover
    &:active
      color #fff
    &[disabled]
      color $c_dim
      pointer-events none
      opacity 0.9
      &:after
        background-color #fff

  &__prices
    margin-right auto
    display flex
    height 100%
    max-width calc(100% - 32em)
    &_many
      flex-wrap wrap

  &__price
    display flex
    align-items center
    justify-content center
    font-size 1em
    padding 0 0.8em
    height 100%
    background-color $c_empty
    margin-right 0.3em
    &:first-child
      margin-left 0
    ~/__prices_many &
      font-size 0.9em
      padding 0 0.6em
      margin-bottom 0.3em
      height calc(50% - 0.15em)

  &__total
    margin-right 1.5em
    font-size 1.25em

  &__actions
    .button
      margin-left 0.5em

  &__scheme
    display flex
    width 'calc(100% + %s)' % ($scroll_width * 1px)
    height 'calc(100% + %s)' % ($scroll_width * 1px)
    overflow scroll
    margin-right $scroll_width * -1px
    margin-bottom $scroll_width * -1px
    padding 1em 0

  // SVG

  &__canvas
    display block
    margin auto
    font-family sans-serif
    font-size 1.25em
    flex 0 0 auto
    *
      pointer-events none

  &__label
    font-size 0.9em
    font-weight 400
    fill $c_dim

  &__text
    font-weight 700
    fill $c_text

  &__seat
    rect
      pointer-events auto
      cursor pointer
    text
      font-weight 700

    &_disabled
      rect
        fill lighten($c_bg, 25%) !important
        pointer-events none
      text
        display none

    &_selected
      rect
        fill $c_error
      text
        fill #fff

  .hall-scheme-scene
    & + .hall__text
      font-size 1.4em
      fill #fff
</style>
